/*
 * recMaintenance.vue のおすすめ整備一覧、WorkAndBuhin 接続、
 * 保存キュー、表示切替、手入力おすすめ追加を切り出した処理。
 */
export interface RecMaintenanceWorkToggleState {
  showWork: boolean;
  activeInspect: any;
}

export interface RecMaintenanceCloseEvent {
  isChange?: boolean;
  isResetSeibi?: boolean;
  isSocket?: boolean;
}

export interface RecMaintenanceCloseState {
  shouldSave: boolean;
  isSocket: boolean;
  activeInspect: any;
}

export interface RecMaintenanceManualBuhinDeleteState {
  shouldClear: boolean;
  manualBuhinList: any[];
}

export interface RecMaintenanceCurrentDataState {
  hasCategory: boolean;
  kingakuFlg: boolean;
  currentData: any[];
}

export interface RecMaintenanceSaveQueueItemBase {
  status: number;
  item: any;
}

export interface RecMaintenanceHideAllItemState {
  shouldUpdate: boolean;
  state: any;
}

export interface RecMaintenanceSaveSuccessState {
  hasPendingSave: boolean;
  pendingItems: any[];
  tempWaitSave: any[];
  tempListSave: any[];
  arrStatus: number[];
}

export interface RecMaintenanceManualInputResetState {
  seibiNm: string;
  kouchinZn: any;
  kingakuFlg: boolean;
}

export interface RecMaintenanceCategoryStatusInput {
  nItem: number;
  sumStatus: number;
}

export interface RecMaintenanceChangeShowState {
  action: string;
  itemState: any;
  activeInspect: any;
}

export interface RecMaintenanceConfirmChangeState {
  activeInspect: any;
  itemState: any;
  shouldClearBuhin: boolean;
  status: number;
}

/*
 * showWork を recMaintenance.vue から分離し、
 * WorkAndBuhin の開閉と activeInspect 切替の state を固定するため。
 */
export function buildRecMaintenanceWorkToggleState(item: any): RecMaintenanceWorkToggleState {
  const showWork = !(item && item.showWork);
  return {
    showWork,
    activeInspect: showWork ? item : {}
  };
}

/*
 * openConfirm を recMaintenance.vue から分離し、
 * 確認 dialog の対象を親の activeInspect に固定するため。
 */
export function resolveRecMaintenanceConfirmItem(activeInspect: any): any {
  return activeInspect;
}

/*
 * onclose を recMaintenance.vue から分離し、
 * WorkAndBuhin close event から保存要否と socket 通知要否を決めるため。
 */
export function buildRecMaintenanceCloseState(
  event: RecMaintenanceCloseEvent,
  activeInspect: any
): RecMaintenanceCloseState {
  const shouldSave = !!(event && (event.isResetSeibi || event.isChange));
  return {
    shouldSave,
    isSocket: shouldSave && !!event.isSocket,
    activeInspect: shouldSave ? activeInspect : null
  };
}

/*
 * template の選択ボタン disabled 判定を recMaintenance.vue から分離し、
 * osusumeIsShow による操作可否を固定するため。
 */
export function isRecMaintenanceWorkDisabled(item: any): boolean {
  return !(item && item.osusumeIsShow);
}

/*
 * template の選択ボタン文言を recMaintenance.vue から分離し、
 * buhinSelected から「選択」/「選択済」を決める契約を固定するため。
 */
export function getRecMaintenanceWorkButtonLabel(item: any): string {
  return item && item.buhinSelected == 0 ? "選択" : "選択済";
}

/*
 * template の選択ボタン class 判定を recMaintenance.vue から分離し、
 * 選択済、未完了、通常表示の分岐を固定するため。
 */
export function getRecMaintenanceWorkButtonClass(
  item: any,
  isIncomplete: boolean
): string {
  if (item && item.buhinSelected != null && item.buhinSelected > 0) {
    return "green";
  }
  if (isIncomplete) {
    return "red";
  }
  return "normal bold";
}

/*
 * template の提案表示ボタン class 判定を recMaintenance.vue から分離し、
 * osusumeIsShow から表示状態の class を決めるため。
 */
export function getRecMaintenanceShowButtonClass(item: any): string {
  return item && item.osusumeIsShow ? "green" : "normal bold";
}

/*
 * allShow を recMaintenance.vue から分離し、
 * カテゴリ内のおすすめ表示状態が指定値で揃っているかを固定するため。
 */
export function isRecMaintenanceShowAll(items: any[], show: number): boolean {
  return Array.isArray(items) && items.every(item => item.osusumeIsShow == show);
}

/*
 * save 時の socket 判定を recMaintenance.vue から分離し、
 * 保存済 status と部品候補数から通知要否を決める契約を固定するため。
 */
export function resolveRecMaintenanceSocketFlag(
  arrStatus: number[],
  completedStatus: number,
  activeInspect: any
): number {
  let isSocket = Array.isArray(arrStatus) && arrStatus.indexOf(completedStatus) > -1
    ? 1
    : 0;
  if (isSocket === 0) {
    if (activeInspect && activeInspect.buhinList) {
      if (activeInspect.buhinList.length === 0 || activeInspect.buhinList.length === 1) {
        isSocket = 1;
      }
    }
    if (activeInspect && activeInspect.osusumeBuhinList) {
      if (
        activeInspect.osusumeBuhinList.length === 0 ||
        activeInspect.osusumeBuhinList.length === 1
      ) {
        isSocket = 1;
      }
    }
  }
  return isSocket;
}

/*
 * 保存キュー検索を recMaintenance.vue から分離し、
 * sknRecommendInspecSeibiId または osusumeId で同一整備を探す契約を固定するため。
 */
export function findRecMaintenanceSeibiIndex(list: any[], seibiSave: any): number {
  const items = Array.isArray(list) ? list : [];
  return items.findIndex(
    (seibi: any) =>
      (seibi.sknRecommendInspecSeibiId &&
        seibi.sknRecommendInspecSeibiId ==
          seibiSave.sknRecommendInspecSeibiId) ||
      (seibi.osusumeId && seibi.osusumeId == seibiSave.osusumeId)
  );
}

/*
 * 手入力部品削除後の state を recMaintenance.vue から分離し、
 * manualBuhinList の array fallback とクリア要否を固定するため。
 */
export function buildRecMaintenanceManualBuhinDeleteState(
  item: any
): RecMaintenanceManualBuhinDeleteState {
  const manualBuhinList =
    item && Array.isArray(item.manualBuhinList)
      ? item.manualBuhinList.slice()
      : [];
  return {
    shouldClear: manualBuhinList.length > 0,
    manualBuhinList
  };
}

/*
 * setCurrentData を recMaintenance.vue から分離し、
 * activeCategory のおすすめ一覧と金額表示 flag を作る契約を固定するため。
 */
export function buildRecMaintenanceCurrentDataState(
  data: any,
  activeCategory: number
): RecMaintenanceCurrentDataState {
  const category =
    data &&
    data.recommend &&
    data.recommend.length &&
    data.recommend[activeCategory]
      ? data.recommend[activeCategory]
      : null;
  if (!category) {
    return {
      hasCategory: false,
      kingakuFlg: false,
      currentData: []
    };
  }
  const kingakuZn = category.kingakuZn ? category.kingakuZn : null;
  const kingakuZk = category.kingakuZk ? category.kingakuZk : null;
  const subItems = Array.isArray(category.subItems) ? category.subItems : [];
  return {
    hasCategory: true,
    kingakuFlg: !!(
      (kingakuZn && kingakuZn > 0) ||
      (kingakuZk && kingakuZk > 0)
    ),
    currentData: subItems.map((item: any) => ({
      ...item,
      isSaving: false
    }))
  };
}

/*
 * カテゴリ status 判定を recMaintenance.vue から分離し、
 * カテゴリ内 item 数と status 合計から none/completed/incomplete を決めるため。
 */
export function resolveRecMaintenanceCategoryStatus(
  sumStatus: number,
  nItem: number,
  noneStatus: number,
  completedStatus: number,
  incompleteStatus: number
): number {
  if (sumStatus == 0) {
    return noneStatus;
  }
  if (sumStatus == nItem * completedStatus) {
    return completedStatus;
  }
  return incompleteStatus;
}

/*
 * カテゴリ status 判定の入力生成を recMaintenance.vue から分離し、
 * currentData の件数と status 合計を固定するため。
 */
export function buildRecMaintenanceCategoryStatusInput(
  currentData: any[]
): RecMaintenanceCategoryStatusInput {
  const items = Array.isArray(currentData) ? currentData : [];
  return {
    nItem: items.length,
    sumStatus: items.reduce(
      (sum: number, item: any) => sum + (item.status ? item.status : 0),
      0
    )
  };
}

/*
 * route 復元用のカテゴリ ID 解決を recMaintenance.vue から分離し、
 * recommend 配列の position から sknInspectCategoryId を取り出す契約を固定するため。
 */
export function resolveRecMaintenanceCategoryIdByPosition(
  recommend: any,
  pos: any
): any {
  const categories = Array.isArray(recommend) ? recommend : [];
  const index = Number(pos);
  const category = categories[index];
  return category && category.sknInspectCategoryId
    ? category.sknInspectCategoryId
    : undefined;
}

/*
 * category クリック時の route info 生成を recMaintenance.vue から分離し、
 * apiPath、カテゴリ ID、position の保存形式を固定するため。
 */
export function buildRecMaintenanceRouteInfo(
  recommend: any,
  pos: number
): any {
  return {
    apiPath: "recommend",
    sknInspectCategoryId: resolveRecMaintenanceCategoryIdByPosition(
      recommend,
      pos
    ),
    pos,
    subPos: 0
  };
}

/*
 * 最後に選択したカテゴリ position の復元を recMaintenance.vue から分離し、
 * category 情報が残っている場合だけ pos を返す契約を固定するため。
 */
export function resolveRecMaintenanceLastSelectedPos(
  hasLastSelectedCategoryId: boolean,
  lastCategoryInfo: any
): number {
  if (
    hasLastSelectedCategoryId &&
    lastCategoryInfo &&
    lastCategoryInfo.pos != null
  ) {
    return lastCategoryInfo.pos;
  }
  return -1;
}

/*
 * 最後に選択したカテゴリの復元可否を recMaintenance.vue から分離し、
 * 保存済み pos が現在のカテゴリ数内にあるかを固定するため。
 */
export function resolveRecMaintenanceCategoryRestorePos(
  hasLastSelectedCategoryId: boolean,
  lastCategoryInfo: any,
  length: number
): number {
  const pos = resolveRecMaintenanceLastSelectedPos(
    hasLastSelectedCategoryId,
    lastCategoryInfo
  );
  if (pos > -1 && length > pos) {
    return Number(pos);
  }
  return -1;
}

/*
 * カテゴリ一括保存 item 生成を recMaintenance.vue から分離し、
 * categoryId 付与、非表示 item の deleteStatus 化、表示用 field 削除を固定するため。
 */
export function buildRecMaintenanceCurrentDataSaveItems(
  items: any[],
  categoryId: any,
  deleteStatus: number
): any[] {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item: any) => {
    const saveItem = {
      ...item,
      sknRecommendInspectCategoryId: categoryId,
      buhinSelected:
        item.osusumeIsShow == 0 ? deleteStatus : item.buhinSelected
    };
    delete saveItem.status;
    delete saveItem.showWork;
    delete saveItem.isSaving;
    return saveItem;
  });
}

/*
 * 通常部品の保存用 list 生成を recMaintenance.vue から分離し、
 * 保存 payload に必要な部品 field と master 金額 fallback を固定するため。
 */
export function buildRecMaintenanceBuhinSaveList(buhinList: any): any[] {
  if (!Array.isArray(buhinList) || buhinList.length === 0) {
    return [];
  }
  return buhinList.map((subElement: any) => ({
    buhinCd: subElement.buhinCd,
    buhinId: subElement.buhinId,
    buhinNm: subElement.buhinNm,
    buhinPriceZk: subElement.buhinPriceZk,
    buhinPriceZn: subElement.buhinPriceZn,
    buhinShubetsuId: subElement.buhinShubetsuId,
    isDelete: subElement.isDelete,
    isExtreme: subElement.isExtreme,
    kingakuZk: subElement.kingakuZk,
    kingakuZn: subElement.kingakuZn,
    quantity: subElement.quantity,
    quantityStep: subElement.quantityStep,
    id: subElement.id ? subElement.id : null,
    buhinMasterZk: subElement.buhinMasterZk ? subElement.buhinMasterZk : 0,
    buhinMasterZn: subElement.buhinMasterZn ? subElement.buhinMasterZn : 0
  }));
}

/*
 * おすすめ部品の保存用 list 生成を recMaintenance.vue から分離し、
 * osusumeBuhinList の保存 field と master 金額 fallback を固定するため。
 */
export function buildRecMaintenanceOsusumeBuhinSaveList(
  osusumeBuhinList: any
): any[] {
  if (!Array.isArray(osusumeBuhinList) || osusumeBuhinList.length === 0) {
    return [];
  }
  return osusumeBuhinList.map((subElement: any) => ({
    buhinCd: subElement.buhinCd,
    buhinId: subElement.buhinId,
    buhinLinkId: subElement.buhinLinkId,
    buhinNm: subElement.buhinNm,
    buhinOsusumeId: subElement.buhinOsusumeId,
    buhinPriceZk: subElement.buhinPriceZk,
    buhinPriceZn: subElement.buhinPriceZn,
    buhinShubetsuId: subElement.buhinShubetsuId,
    id: subElement.id,
    isDelete: subElement.isDelete,
    isExtreme: subElement.isExtreme,
    kingakuExtensionId: subElement.kingakuExtensionId,
    kingakuZk: subElement.kingakuZk,
    kingakuZn: subElement.kingakuZn,
    koukanMeyasuTime: subElement.koukanMeyasuTime,
    kousuu: subElement.kousuu,
    quantity: subElement.quantity,
    quantityStep: subElement.quantityStep,
    buhinMasterZk: subElement.buhinMasterZk ? subElement.buhinMasterZk : 0,
    buhinMasterZn: subElement.buhinMasterZn ? subElement.buhinMasterZn : 0
  }));
}

/*
 * 通常おすすめ item の保存形生成を recMaintenance.vue から分離し、
 * hidden item の deleteStatus、部品 list、カテゴリ ID 付与を固定するため。
 */
export function buildRecMaintenanceSubItemSave(params: {
  element: any;
  buhinList: any[];
  osusumeBuhinList: any[];
  categoryId: any;
  deleteStatus: number;
}): any {
  const element = params.element;
  return {
    branchId: element.branchId,
    buhinBunruiList: element.buhinBunruiList ? element.buhinBunruiList : [],
    buhinList: params.buhinList,
    buhinSelected:
      element.osusumeIsShow == 0 ? params.deleteStatus : element.buhinSelected,
    inspectResult: element.inspectResult,
    inspectValue: element.inspectValue,
    isDoTenken: element.isDoTenken,
    isInspectDuplicate: element.isInspectDuplicate,
    isNumber: element.isNumber,
    kouchinZn: element.kouchinZn,
    koukanMeyasuTime: element.koukanMeyasuTime,
    manualBuhinList: element.manualBuhinList ? element.manualBuhinList : [],
    osusumeBuhinList: params.osusumeBuhinList,
    osusumeId: element.osusumeId ? element.osusumeId : null,
    osusumeIsShow: element.osusumeIsShow,
    seibiId: element.seibiId,
    sknInspectSeibiDetailId: element.sknInspectSeibiDetailId,
    sknInspectType: element.sknInspectType,
    sknRecommendInspecSeibiId: element.sknRecommendInspecSeibiId,
    sknRecommendInspecSeibiNm: element.sknRecommendInspecSeibiNm,
    sknRecommendInspectCategoryId: params.categoryId,
    sknUketsukeExtendsId: element.sknUketsukeExtendsId,
    inspectQuantity: element.inspectQuantity ? element.inspectQuantity : null,
    kousuu: element.kousuu ? element.kousuu : null,
    buhinShubetsuCd: element.buhinShubetsuCd ? element.buhinShubetsuCd : null,
    kouchinZk: element.kouchinZk ? element.kouchinZk : null,
    isExtreme: element.isExtreme,
    workCd: element.workCd
  };
}

/*
 * 手入力おすすめ item の保存形生成を recMaintenance.vue から分離し、
 * uketsukeExtendsId、status、カテゴリ ID、部品 list の詰め方を固定するため。
 */
export function buildRecMaintenanceManualItemSave(params: {
  element: any;
  buhinList: any[];
  osusumeBuhinList: any[];
  uketsukeExtendsId: any;
  categoryId: any;
  doneStatus: number;
  deleteStatus: number;
  recommendManualType: number;
}): any {
  const element = params.element;
  return {
    sknUketsukeExtendsId: params.uketsukeExtendsId + "",
    seibiId: element.seibiId,
    seibiNm: element.sknRecommendInspecSeibiNm,
    kouchinZn: element.kouchinZn,
    kouchinZk: element.kouchinZk,
    buhinSelected:
      element.osusumeIsShow == 1 ? params.doneStatus : params.deleteStatus,
    sknInspectType: params.recommendManualType,
    sknRecommendInspectCategoryId: params.categoryId,
    osusumeIsShow: element.osusumeIsShow,
    buhinList: params.buhinList,
    manualBuhinList: element.manualBuhinList,
    osusumeBuhinList: params.osusumeBuhinList
  };
}

/*
 * currentData の一括保存 payload 生成を recMaintenance.vue から分離し、
 * 通常おすすめと手入力おすすめの分類を固定するため。
 */
export function buildRecMaintenanceCurrentDataSavePayload(params: {
  items: any[];
  category: any;
  uketsukeExtendsId: any;
  doneStatus: number;
  deleteStatus: number;
  recommendManualType: number;
}): any {
  const items = Array.isArray(params.items) ? params.items : [];
  const category = params.category || {};
  const categoryId = category.sknRecommendInspectCategoryId;
  const manualItems = items
    .filter((item: any) => {
      return item.osusumeId == null && item.sknRecommendInspecSeibiId == null;
    })
    .map((item: any) =>
      buildRecMaintenanceManualItemSave({
        element: item,
        buhinList: item.buhinList,
        osusumeBuhinList: item.osusumeBuhinList,
        uketsukeExtendsId: params.uketsukeExtendsId,
        categoryId: categoryId
          ? categoryId
          : item.sknRecommendInspectCategoryId,
        doneStatus: params.doneStatus,
        deleteStatus: params.deleteStatus,
        recommendManualType: params.recommendManualType
      })
    );
  const subItems = items.filter((item: any) => {
    return item.osusumeId != null || item.sknRecommendInspecSeibiId != null;
  });
  return {
    isSocket: 1,
    recommend: [
      {
        sknRecommendInspectCategoryId: categoryId,
        sknRecommendInspectCategoryNm: category.sknRecommendInspectCategoryNm,
        kingakuZk: category.kingakuZk,
        kingakuZn: category.kingakuZn,
        subItems,
        manualItems
      }
    ]
  };
}

/*
 * 保存対象 item の subItems/manualItems 分類を recMaintenance.vue から分離し、
 * 通常おすすめと手入力おすすめの保存 group 生成を固定するため。
 */
export function buildRecMaintenanceSaveItemGroups(params: {
  items: any[];
  data: any;
  activeCategory: number;
  uketsukeExtendsId: any;
  doneStatus: number;
  deleteStatus: number;
  recommendManualType: number;
}): any {
  const subItems: any[] = [];
  const manualItems: any[] = [];
  const items = Array.isArray(params.items) ? params.items : [];
  items.forEach((element: any) => {
    const buhinList = buildRecMaintenanceBuhinSaveList(element.buhinList);
    const osusumeBuhinList = buildRecMaintenanceOsusumeBuhinSaveList(
      element.osusumeBuhinList
    );
    const categoryId = resolveRecMaintenanceCategoryId(
      params.data,
      params.activeCategory,
      element.sknRecommendInspectCategoryId
    );
    if (element.osusumeId != null || element.sknRecommendInspecSeibiId != null) {
      subItems.push(
        buildRecMaintenanceSubItemSave({
          element,
          buhinList,
          osusumeBuhinList,
          categoryId,
          deleteStatus: params.deleteStatus
        })
      );
    } else {
      manualItems.push(
        buildRecMaintenanceManualItemSave({
          element,
          buhinList,
          osusumeBuhinList,
          uketsukeExtendsId: params.uketsukeExtendsId,
          categoryId,
          doneStatus: params.doneStatus,
          deleteStatus: params.deleteStatus,
          recommendManualType: params.recommendManualType
        })
      );
    }
  });
  return {
    subItems,
    manualItems
  };
}

/*
 * osusumeBuhinList の保存前整形を recMaintenance.vue から分離し、
 * UI 用 isSelected を payload から除去する契約を固定するため。
 */
export function buildRecMaintenanceOsusumeBuhinListWithoutSelection(
  osusumeBuhinList: any[]
): any[] {
  if (!Array.isArray(osusumeBuhinList)) {
    return osusumeBuhinList;
  }
  return osusumeBuhinList.map((item: any) => {
    const result = {
      ...item
    };
    if (result.hasOwnProperty("isSelected")) {
      delete result.isSelected;
    }
    return result;
  });
}

/*
 * 保存キュー item のカテゴリ ID 補完を recMaintenance.vue から分離し、
 * activeCategory から保存対象カテゴリを固定するため。
 */
export function buildRecMaintenanceSaveQueueItemWithCategory(params: {
  item: any;
  data: any;
  activeCategory: number;
}): any {
  const item = params.item || {};
  if (params.data && params.data.recommend) {
    return {
      ...item,
      sknRecommendInspectCategoryId: resolveRecMaintenanceCategoryId(
        params.data,
        params.activeCategory,
        item.sknRecommendInspectCategoryId
      )
    };
  }
  return item;
}

/*
 * 保存キュー item の基本整形を recMaintenance.vue から分離し、
 * status 抽出と表示用 field 削除を固定するため。
 */
export function buildRecMaintenanceSaveQueueItemBase(
  inspectItem: any
): RecMaintenanceSaveQueueItemBase {
  const item = inspectItem
    ? JSON.parse(JSON.stringify(inspectItem))
    : {};
  const status = item.status || 0;
  delete item.status;
  delete item.showWork;
  delete item.isSaving;
  return {
    status,
    item
  };
}

/*
 * 保存キュー upsert を recMaintenance.vue から分離し、
 * 同一おすすめ整備は置換し、未登録なら追加する契約を固定するため。
 */
export function upsertRecMaintenanceSaveQueue(
  list: any[],
  seibiSave: any
): any[] {
  const items = Array.isArray(list) ? list.slice() : [];
  const index = findRecMaintenanceSeibiIndex(items, seibiSave);
  if (index > -1) {
    items[index] = seibiSave;
  } else {
    items.push(seibiSave);
  }
  return items;
}

/*
 * 一括表示時の item state を recMaintenance.vue から分離し、
 * 部品候補数に応じた buhinSelected と部品 list クリア条件を固定するため。
 */
export function buildRecMaintenanceShowAllItemState(
  item: any,
  noNeedStatus: number,
  doneStatus: number,
  initialStatus: number
): any {
  const state: any = {
    osusumeIsShow: 1
  };
  if (
    item.buhinList &&
    item.buhinList.length == 0 &&
    item.osusumeBuhinList &&
    item.osusumeBuhinList.length == 0
  ) {
    state.buhinSelected = noNeedStatus;
  } else if (
    !item.osusumeBuhinList &&
    item.buhinBunruiList &&
    item.buhinBunruiList.length == 0 &&
    item.buhinList &&
    item.buhinList.length == 0
  ) {
    state.buhinSelected = noNeedStatus;
  } else if (item.osusumeBuhinList && item.osusumeBuhinList.length == 1) {
    state.buhinSelected = doneStatus;
  } else if (item.buhinList && item.buhinList.length == 1) {
    state.buhinSelected = doneStatus;
  } else {
    state.buhinSelected = initialStatus;
    if (item.buhinList && item.buhinList.length > 0) {
      state.buhinList = [];
    }
    if (item.osusumeBuhinList && item.osusumeBuhinList.length > 0) {
      state.osusumeBuhinList = [];
    }
  }
  return state;
}

/*
 * 一括非表示時の item state を recMaintenance.vue から分離し、
 * 重複点検でない item だけ osusumeIsShow=0 にする契約を固定するため。
 */
export function buildRecMaintenanceHideAllItemState(
  item: any
): RecMaintenanceHideAllItemState {
  if (item && item.isInspectDuplicate == 0) {
    return {
      shouldUpdate: true,
      state: {
        osusumeIsShow: 0
      }
    };
  }
  return {
    shouldUpdate: false,
    state: {}
  };
}

/*
 * 表示切替ボタン押下時の分岐を recMaintenance.vue から分離し、
 * 重複エラー、即保存、確認 dialog の action を固定するため。
 */
export function buildRecMaintenanceChangeShowState(
  item: any
): RecMaintenanceChangeShowState {
  if (item && item.isInspectDuplicate && item.osusumeIsShow) {
    return {
      action: "error",
      itemState: {},
      activeInspect: null
    };
  }
  if (item && (item.buhinSelected == 0 || item.osusumeIsShow == 0)) {
    const osusumeIsShow = item.osusumeIsShow ? 0 : 1;
    const itemState: any = {
      osusumeIsShow,
      osusumeBuhinList: null,
      buhinList: []
    };
    if (osusumeIsShow == 0) {
      itemState.inspectResult = 0;
    }
    return {
      action: "save",
      itemState,
      activeInspect: item
    };
  }
  return {
    action: "confirm",
    itemState: {},
    activeInspect: item
  };
}

/*
 * 非表示確認後の item state を recMaintenance.vue から分離し、
 * 部品クリア要否と completed status を返す契約を固定するため。
 */
export function buildRecMaintenanceConfirmChangeState(
  item: any,
  completedStatus: number,
  initialStatus: number
): RecMaintenanceConfirmChangeState {
  const shouldClearBuhin = !!(item && item.isInspectDuplicate == 0);
  const itemState: any = {
    osusumeIsShow: 0,
    inspectResult: 0
  };
  if (shouldClearBuhin) {
    itemState.buhinSelected = initialStatus;
  }
  return {
    activeInspect: {},
    itemState,
    shouldClearBuhin,
    status: completedStatus
  };
}

/*
 * tooltipMessage を recMaintenance.vue から分離し、
 * 重複点検かどうかで編集不可/編集案内の文言を決めるため。
 */
export function getRecMaintenanceTooltipMessage(
  isDuplicate: boolean
): string {
  if (isDuplicate) {
    return "※現車確認又は57項目点検で金額を確定しているため、編集出来ません。現車確認又は57項目点検で編集してください。";
  }
  return "※金額を編集したい場合は、「提案する」を押した後、「選択」後に表示される画面で行ってください。";
}

/*
 * beforeDestroy の保存要否を recMaintenance.vue から分離し、
 * timerSave、保存キュー、route 名、quick route の条件を固定するため。
 */
export function shouldRecMaintenanceSaveBeforeDestroy(params: {
  timerSave: any;
  tempListSave: any[];
  routeName: any;
  quickRoutes: string[];
}): boolean {
  if (!params.timerSave) {
    return false;
  }
  if (!Array.isArray(params.tempListSave) || params.tempListSave.length === 0) {
    return false;
  }
  if (typeof params.routeName === "undefined") {
    return false;
  }
  return !params.quickRoutes.includes(params.routeName);
}

/*
 * save success 後の state を recMaintenance.vue から分離し、
 * pending save の有無と保存キュー/状態配列のリセットを固定するため。
 */
export function buildRecMaintenanceSaveSuccessState(
  tempWaitSave: any[]
): RecMaintenanceSaveSuccessState {
  const pendingItems = Array.isArray(tempWaitSave) ? tempWaitSave : [];
  return {
    hasPendingSave: pendingItems.length > 0,
    pendingItems,
    tempWaitSave: [],
    tempListSave: [],
    arrStatus: []
  };
}

/*
 * 手入力おすすめ登録後の input reset を recMaintenance.vue から分離し、
 * 名称、金額、金額 flag の初期化 state を固定するため。
 */
export function buildRecMaintenanceManualInputResetState(): RecMaintenanceManualInputResetState {
  return {
    seibiNm: "",
    kouchinZn: null,
    kingakuFlg: false
  };
}

/*
 * recommend 保存 payload wrapper を recMaintenance.vue から分離し、
 * isSocket、subItems、manualItems の送信形を固定するため。
 */
export function buildRecMaintenanceSavePayload(params: {
  isSocket: number;
  subItems: any[];
  manualItems: any[];
}): any {
  return {
    isSocket: params.isSocket,
    recommend: [
      {
        subItems: params.subItems,
        manualItems: params.manualItems
      }
    ]
  };
}

/*
 * save 対象 item 解決を recMaintenance.vue から分離し、
 * 単体保存 item があれば優先し、なければ保存キューを使う契約を固定するため。
 */
export function buildRecMaintenanceSaveTargetItems(
  item: any,
  tempListSave: any[]
): any[] {
  if (item) {
    return ([] as any[]).concat(item);
  }
  return Array.isArray(tempListSave) ? tempListSave : [];
}

/*
 * 保存用カテゴリ ID 解決を recMaintenance.vue から分離し、
 * activeCategory のカテゴリ ID または fallback を使う契約を固定するため。
 */
export function resolveRecMaintenanceCategoryId(
  data: any,
  activeCategory: number,
  fallbackCategoryId: any = 0
): any {
  if (
    data &&
    data.recommend &&
    data.recommend[activeCategory] &&
    data.recommend[activeCategory].sknRecommendInspectCategoryId
  ) {
    return data.recommend[activeCategory].sknRecommendInspectCategoryId;
  }
  return fallbackCategoryId;
}

/*
 * 手入力おすすめ追加 payload を recMaintenance.vue から分離し、
 * 入力値から API 保存用 item を作る契約を固定するため。
 */
export function buildRecMaintenanceManualAddItem(params: {
  uketsukeExtendsId: any;
  seibiNm: string;
  kouchinZn: any;
  doneStatus: number;
  recommendManualType: number;
  categoryId: any;
}): any {
  return {
    sknUketsukeExtendsId: params.uketsukeExtendsId + "",
    sknRecommendInspecSeibiNm: params.seibiNm,
    kouchinZn: params.kouchinZn,
    buhinSelected: params.doneStatus,
    sknInspectType: params.recommendManualType,
    osusumeIsShow: 1,
    sknRecommendInspectCategoryId: params.categoryId
  };
}

/*
 * 手入力おすすめ保存後の画面 item 生成を recMaintenance.vue から分離し、
 * レスポンス seibiId と入力値から currentData に追加する形を固定するため。
 */
export function buildRecMaintenanceAddedManualItem(params: {
  uketsukeExtendsId: any;
  seibiId: any;
  seibiNm: string;
  kouchinZn: any;
  doneStatus: number;
  completedStatus: number;
  recommendManualType: number;
  categoryId: any;
  rowNum: number;
}): any {
  return {
    sknUketsukeExtendsId: params.uketsukeExtendsId,
    sknInspectType: params.recommendManualType,
    branchId: "0",
    inspectResult: 0,
    isNumber: 0,
    inspectValue: 0,
    seibiId: params.seibiId,
    sknInspectCommentId: 0,
    inspectValueList: [],
    inspectValueViewType: 0,
    koukanMeyasuTime: 0,
    sknRecommendInspecSeibiId: null,
    sknRecommendInspecSeibiNm: params.seibiNm,
    sknInspectSeibiDetailId: 0,
    isInspectDuplicate: 0,
    kingaku: params.kouchinZn,
    kouchinZn: params.kouchinZn,
    buhinSelected: params.doneStatus,
    buhinList: [],
    manualBuhinList: [],
    osusumeIsShow: 1,
    sknRecommendInspectCategoryId: params.categoryId,
    osusumeBuhinList: [],
    isDoTenken: 0,
    rowNum: params.rowNum,
    isSaving: false,
    status: params.completedStatus
  };
}

/*
 * 保存完了 item の key 抽出を recMaintenance.vue から分離し、
 * レスポンス subItems から保存完了判定用の id/isExtreme を作るため。
 */
export function buildRecMaintenanceSavingCompleteKeys(data: any): any[] {
  const subItems =
    data &&
    data.recommend &&
    data.recommend[0] &&
    Array.isArray(data.recommend[0].subItems)
      ? data.recommend[0].subItems
      : [];
  return subItems.map((item: any) => ({
    id: item.sknRecommendInspecSeibiId,
    isExtreme: item.isExtreme
  }));
}

/* 保存した作業だけロックを解除し、同じ作業の後続保存があれば維持する。 */
export function clearRecMaintenanceSavingState(params: {
  currentData: any[];
  savePayload: any;
  pendingItems: any[];
}): any[] {
  const savedItems: any[] = [];
  (params.savePayload.recommend || []).forEach((category: any) => {
    savedItems.push(...(category.subItems || []), ...(category.manualItems || []));
  });
  const matches = (left: any, right: any) =>
    (left.seibiId != null && left.seibiId == right.seibiId) ||
    (left.osusumeId != null && left.osusumeId == right.osusumeId) ||
    (left.sknRecommendInspecSeibiId != null &&
      left.sknRecommendInspecSeibiId == right.sknRecommendInspecSeibiId &&
      left.isExtreme == right.isExtreme);
  return params.currentData.map((item: any) => {
    if (!savedItems.some(saved => matches(item, saved))) {
      return item;
    }
    return {
      ...item,
      isSaving: params.pendingItems.some(pending => matches(item, pending))
    };
  });
}
